/**
 * <Confetti> — la pluie de confettis d'un temps fort (atom).
 *
 * Les deux pluies de l'ancien code, copiées dans les assets du design system (le DS n'importe rien de
 * `common/`), en attendant les animations des champions V16 :
 *  - `activity` : la fin d'une activité (`ActivityFinalizedCongrats`, `confetti-2.json`), tout de
 *    suite, une fois, qui couvre la surface et reste sur sa dernière image ;
 *  - `title` : un nouveau titre (`CongratsScreen.js`, `62717-confetti.json`), qui part au bout d'une
 *    seconde et se retire à cinq, comme l'ancienne (`setShow` à 1 s puis 5 s).
 *
 * Elle ne prend jamais le toucher (`pointerEvents="none"`) et se pose par-dessus ce qui la porte :
 * le parent lui donne sa place (`position: relative` par défaut d'une vue). Moins d'animation
 * demandé par le système : elle ne se monte pas — le résultat se lit sans elle.
 *
 * `lottie-react-native` est encapsulé ici et nulle part ailleurs dans le design system.
 */
import LottieView, { type AnimationObject } from "lottie-react-native"
import React, { useEffect, useState } from "react"
import { StyleSheet as RNStyleSheet, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import activityConfetti from "../../assets/animations/confetti-2.json"
import titleConfetti from "../../assets/animations/title-confetti.json"
import { useReduceMotion } from "../../hooks/useReduceMotion"

/** Les pluies que le design system connaît : la fin d'une activité, un nouveau titre. */
export type ConfettiVariant = "activity" | "title"

type VariantSpec = {
    source: AnimationObject
    /** Avant la pluie. */
    delayMs: number
    /** Le temps de la pluie ; `null` : elle reste sur sa dernière image. */
    durationMs: number | null
    resizeMode?: "cover"
}

const VARIANTS: Record<ConfettiVariant, VariantSpec> = {
    activity: { source: activityConfetti as AnimationObject, delayMs: 0, durationMs: null, resizeMode: "cover" },
    title: { source: titleConfetti as AnimationObject, delayMs: 1000, durationMs: 4000 },
}

export type ConfettiProps = {
    /** Défaut `title`. */
    variant?: ConfettiVariant
    /** Avant la pluie — celui de la variante par défaut (`title` : 1 000 ms, `activity` : tout de suite). */
    delayMs?: number
    /** Le temps de la pluie — celui de la variante par défaut (`title` : 4 000 ms, `activity` : elle reste). */
    durationMs?: number | null
    testID?: string
}

/** Sur le web, la bibliothèque ne lit que `webStyle` : la pluie remplit la vue qui la porte. */
const WEB_FILL = { width: "100%", height: "100%" }

export function Confetti(props: ConfettiProps) {
    const { variant = "title", testID } = props
    const spec = VARIANTS[variant]
    const delayMs = props.delayMs ?? spec.delayMs
    const durationMs = props.durationMs === undefined ? spec.durationMs : props.durationMs
    const reduceMotion = useReduceMotion()
    const [shown, setShown] = useState(delayMs === 0)

    useEffect(() => {
        const start = delayMs > 0 ? setTimeout(() => setShown(true), delayMs) : null
        const end = durationMs != null ? setTimeout(() => setShown(false), delayMs + durationMs) : null
        return () => {
            if (start) clearTimeout(start)
            if (end) clearTimeout(end)
        }
    }, [delayMs, durationMs])

    if (reduceMotion || !shown) return null

    return (
        <View style={styles.fill} pointerEvents="none" testID={testID}>
            {/* Une vue tierce : Unistyles ne la patche pas, elle prend le remplissage de React Native. */}
            <LottieView
                source={spec.source}
                autoPlay
                loop={false}
                resizeMode={spec.resizeMode}
                style={RNStyleSheet.absoluteFill}
                webStyle={WEB_FILL}
            />
        </View>
    )
}

const styles = StyleSheet.create(() => ({
    fill: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
}))
